
import Vue from "vue";
import dayjs from "dayjs";
import XLSX from "xlsx";
import { Chart } from "highcharts-vue";
import ButtonDanger from "@/components/ButtonDanger.vue";
import ButtonReload from "@/components/ButtonReload.vue";
import DateTimeRange from "@/components/DateTimeRange.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import NoDataAvailable from "@/components/NoDataAvailable.vue";
import { McaTimeSeriesTagCodeEnum } from "./../../enums/McaTimeSeriesTagCodeEnum";

export default Vue.extend({
  name: "McaTimeSeries",
  components: {
    highcharts: Chart,
    ButtonDanger,
    ButtonReload,
    DateTimeRange,
    LoadingGraph,
    NoDataAvailable,
  },
  data() {
    const date = new Date();

    return {
      source: null,
      isSensorType: { id: "Ia", name: "IA" },
      isShowDropdownSensorType: true,
      currentSecondaryTabName: McaTimeSeriesTagCodeEnum.CURRENT,
      xAxisMaxRange: 0,
      yAxisMaxRange: 0,
      xAxisMin: undefined,
      xAxisMax: undefined,
      yAxisMin: undefined,
      yAxisMax: undefined,
      sensorTypeData: [
        { id: "Ia", name: "IA" },
        { id: "Ib", name: "IB" },
        { id: "Ic", name: "IC" },
      ],
      selectDateType: "normal",
      startDate: dayjs(date).subtract(30, "day").format("YYYY-MM-DDT00:00:00"),
      endDate: dayjs(date).format("YYYY-MM-DDT23:59:59"),
      isNormal: true,
      chartResultData: [],
      chartLoading: true,
      chartOptions: {
        chart: {
          type: "line",
          height: 450,
          zoomType: "xy",
          borderWidth: 3,
          borderRadius: 6,
          borderColor: "#F1EFEF",
          spacing: [30, 50, 30, 50],
        },
        navigator: {
          enabled: true,
          xAxis: {
            labels: {
              enabled: false,
            },
          },
        },
        scrollbar: {
          enabled: true,
          showFull: false,
          liveRedraw: false,
        },
        title: { text: null },
        subtitle: { text: null },
        tooltip: {
          formatter: function () {
            let tooltipText = `<b>${this.x}</b><br/>`;
            let unit = "-";
            const remaining = this.series.chart.series.filter((val) => val.data.length > 0 && val.visible);
            const { warning, alarm, isAlert } = this.series.options;
            // Show Y value for each series (exclude warning and alarm)
            remaining.forEach((series) => {
              if (series.data.length > 0 && series.visible) {
                tooltipText += `<span style="color:${series.color}">\u25CF</span> <b>${series.name}: </b>${
                  series.yData[this.point.index]
                } ${series.options.tooltip.valueSuffix}<br/>`;
                unit = series.options.tooltip.valueSuffix;
              }
            });

            // Check if the series has only 1 line and add warning/alarm information

            if (remaining.length > 0 && isAlert) {
              tooltipText += `<span style="color:#FCA239">\u25CF</span> <b>Warning: </b>${warning || 0} ${unit}<br/>`;
              tooltipText += `<span style="color:#D9263B">\u25CF</span> <b>Alarm: </b>${alarm || 0} ${unit}<br/>`;
            }

            return tooltipText;
          },
        },
        legend: {
          layout: "horizontal",
          align: "left",
          verticalAlign: "top",
          itemStyle: { fontSize: "12px", fontWeight: "300", color: "#000000" },
        },
        yAxis: {
          min: null,
          max: null,
          plotLines: [],
          title: {
            enabled: true,
            text: "",
          },
        },
        xAxis: {
          type: "category",
          categories: [],
        },
        series: [],
        credits: { enabled: false },
        plotOptions: {
          series: {
            events: {
              legendItemClick: function () {
                const chart = this.chart;
                const series = this.chart.series;
                const seriesIndex = this.index;
                const dataMax = (!this.visible && this.dataMax) || undefined;
                const values = [];
                dataMax && values.push(dataMax);
                this.chart.series.forEach((series, index) => {
                  if (index !== seriesIndex && series.visible) values.push(series.dataMax);
                });
                if (this.name === "All" && this.visible) {
                  series.forEach((val, ind) => {
                    if (ind !== seriesIndex) val.setVisible(false);
                  });
                  return;
                } else if (this.name === "All" && !this.visible) {
                  series.forEach((val, ind) => {
                    if (ind !== seriesIndex) val.setVisible(true);
                  });
                  const remaining = series.filter((val) => val.visible);
                  const seriesY = remaining.map((val) => Math.max(...val.yData));
                  const maxY = Math.max(...[...seriesY]) + 40;

                  this.chart.yAxis[0].update({
                    type: "linear",
                    visible: true,
                    min: null,
                    max: maxY,
                  });

                  this.chart.yAxis[0].removePlotLine("plot-warning");
                  this.chart.yAxis[0].removePlotLine("plot-alarm");
                  return;
                }
                setTimeout(() => {
                  const remaining = series.filter((val) => val.visible);
                  const count = remaining.length;
                  const remainingWithoutAll = series.filter((val) => val.visible && val.data.length > 0);
                  const countWithoutAll = series.filter((val) => val.data.length > 0);
                  if (remainingWithoutAll.length !== countWithoutAll.length) {
                    const all = series.findIndex((val) => val.name === "All");
                    this.chart.series[all].setVisible(false);
                  } else {
                    const all = series.findIndex((val) => val.name === "All");
                    this.chart.series[all].setVisible(true);
                  }
                  if (remainingWithoutAll.length === 1) {
                    const seriesY = remainingWithoutAll[0].yData;
                    const warning = remainingWithoutAll[0].options.warning;
                    const alarm = remainingWithoutAll[0].options.alarm;
                    const isAlert = remainingWithoutAll[0].options.isAlert;

                    const minY =
                      Math.min(...[...seriesY, warning, alarm]) >= 0 ? 0 : Math.min(...[...seriesY, warning, alarm]) - 40;
                    const maxY = Math.max(...[...seriesY, warning, alarm]) + 40;
                    this.chart.yAxis[0].update({
                      type: "linear",
                      visible: true,
                      title: { text: null },
                      min: minY,
                      max: maxY,
                    });
                    if (isAlert) {
                      this.chart.yAxis[0].addPlotLine({
                        id: "plot-warning",
                        value: warning,
                        color: "#FCA239",
                        width: 2,
                        zIndex: 5,
                        dashStyle: "ShortDash",
                      });
                      this.chart.yAxis[0].addPlotLine({
                        id: "plot-alarm",
                        value: alarm,
                        color: "#D9263B",
                        width: 2,
                        zIndex: 5,
                        dashStyle: "ShortDash",
                      });
                    }
                  } else {
                    const seriesY = remaining.map((val) => Math.max(...val.yData));
                    const maxY = Math.max(...[...seriesY]) + 40;

                    this.chart.yAxis[0].update({
                      type: "linear",
                      visible: true,
                      min: null,
                      max: maxY,
                    });

                    this.chart.yAxis[0].removePlotLine("plot-warning");
                    this.chart.yAxis[0].removePlotLine("plot-alarm");
                  }
                }, 100);
              },
              click: function () {
                const chart = this.chart;
                const series = this.chart.series;
                const index = this.index;
                const seriesY = series[index].yData;
                const warning = series[index].options.warning;
                const alarm = series[index].options.alarm;
                const minY =
                  Math.min(...[...seriesY, warning, alarm]) >= 0 ? 0 : Math.min(...[...seriesY, warning, alarm]) - 40;
                const maxY = Math.max(...[...seriesY, warning, alarm]) + 40;
                series.forEach((val, ind) => {
                  if (ind !== index) {
                    val.setVisible(false);
                  }
                });
                setTimeout(() => {
                  this.chart.yAxis[0].update({
                    visible: true,
                    title: { text: null },
                    min: minY,
                    max: maxY,
                    type: "linear",
                  });

                  this.chart.yAxis[0].addPlotLine({
                    id: "plot-warning",
                    value: warning,
                    color: "#FCA239",
                    width: 2,
                    zIndex: 5,
                    dashStyle: "ShortDash",
                  });
                  this.chart.yAxis[0].addPlotLine({
                    id: "plot-alarm",
                    value: alarm,
                    color: "#D9263B",
                    width: 2,
                    zIndex: 5,
                    dashStyle: "ShortDash",
                  });
                }, 100);
              },
            },
          },
        },
      },
    };
  },
  computed: {
    model() {
      return this.$store.getters.model;
    },
    isSecondaryTab() {
      return this.$store.state.graphInformationTab.is_secondary_tab.id;
    },
    secondaryTabName() {
      return this.$store.state.graphInformationTab.is_secondary_tab.name;
    },
    groupCode() {
      return this.$store.state.graphInformationTab.is_secondary_tab.groupCode;
    },
    equipmentCode() {
      return this.$store.state.model.equipmentCode.code;
    },
    primaryTabName() {
      return this.$store.state.graphInformationTab.is_primary_tab.name;
    },
  },
  async created() {
    await this.fetchResultData();
  },
  methods: {
    async fetchResultData() {
      try {
        this.xAxisMin = undefined;
        this.xAxisMax = undefined;
        this.yAxisMin = undefined;
        this.yAxisMax = undefined;
        this.chartLoading = true;

        if (this.source) {
          this.source.cancel();
        }

        const res = await (this as any).$dep.modelUseCase.getMcaResultModelDataNew(
          this.model.modelCode,
          this.isSecondaryTab,
          this.startDate,
          this.endDate,
          this.isNormal,
          this.model.id
        );
        this.chartResultData = res;
        if (this.chartResultData.length === 0) {
          this.chartLoading = false;
          throw "No Data";
        }

        // Set Heading title on top center
        this.chartOptions.title.text = `${this.$store.state.graphInformationTab.is_secondary_tab.name}`;

        this.chartOptions.series = this.series(res);

        this.chartOptions.yAxis.title.text = this.showYAxisLabel(this.currentSecondaryTabName);

        // Set xAxis
        this.chartOptions.xAxis = this.aAxis(res);

        switch (this.isSensorType.name) {
          case "IA":
            this.xAxisMaxRange = this.chartResultData[0].Ia.timestamp.length;
            break;
          case "IB":
            this.xAxisMaxRange = this.chartResultData[0].Ib.timestamp.length;
            break;
          case "IC":
            this.xAxisMaxRange = this.chartResultData[0].Ic.timestamp.length;
            break;
        }

        // Note:McaTime Series  Tab  length = 1
        if (res.length === 1) {
          const warning = res.map((val) => val.warning).flat();
          const alarm = res.map((val) => val.alarm).flat();
          const combined = [
            ...warning,
            ...alarm,
            ...res.map((val) => val.Ia.values).flat(),
            ...res.map((val) => val.Ib.values).flat(),
            ...res.map((val) => val.Ic.values).flat(),
          ];

          const filteredArray = combined.filter((element) => element !== undefined);

          this.chartOptions.yAxis.min = Math.min(...filteredArray) - 20;
          this.chartOptions.yAxis.max = Math.max(...filteredArray) + 20;

          this.chartOptions.yAxis.plotLines = this.chartResultData[0].isAlert
            ? [
                {
                  value: warning[0],
                  width: 2,
                  dashStyle: "dash",
                  color: "#FCA239",
                  zIndex: 5,
                },
                {
                  value: alarm[0],
                  width: 2,
                  dashStyle: "dash",
                  color: "#D9263B",
                  zIndex: 5,
                },
              ]
            : [];
        } else {
          // Note: Frequency detail Tab  length > 1

          // Note: all of line use the same alarm value, then get form alarm index 0
          const alarm = res.map((val) => val.alarm).filter((v, index) => index === 0)[0];
          // Note: all of line use the same warning value, then get form alarm index 0
          const warning = res.map((val) => val.warning).filter((v, index) => index === 0)[0];
          // Note: all of line use the same isAlert value, then get form alarm index 0
          const isAlert = res.map((val) => val.isAlert).filter((v, index) => index === 0)[0];

          this.chartOptions.yAxis.plotLines = isAlert
            ? [
                {
                  value: warning,
                  width: 2,
                  dashStyle: "dash",
                  color: "#FCA239",
                  zIndex: 5,
                },
                {
                  value: alarm,
                  width: 2,
                  dashStyle: "dash",
                  color: "#D9263B",
                  zIndex: 5,
                },
              ]
            : [];

          const combined = [
            ...res.map((val) => val.Ia.values).flat(),
            ...res.map((val) => val.Ib.values).flat(),
            ...res.map((val) => val.Ic.values).flat(),
            alarm,
            warning,
          ];
          const filteredArray = combined.filter((element) => element !== undefined);

          this.chartOptions.yAxis.min = Math.min(...filteredArray) - 20;
          this.chartOptions.yAxis.max = Math.max(...filteredArray) + 20;
        }
        this.chartLoading = false;
      } catch (error) {
        console.log(error);
        this.chartLoading = false;
      } finally {
        this.source = null;
      }
    },
    onChangeSensorType(param: any) {
      const { id, name } = param;
      this.isSensorType = { id, name };
    },
    onChangeSelectDateType(param: any) {
      this.selectDateType = param;
    },
    aAxis(datas: any) {
      const checkSensorType = (val) => {
        let type = "";
        let data = [];
        switch (this.isSensorType.name) {
          case "IA":
            type = "IA";
            data = val[0].Ia.timestamp || [];
            break;
          case "IB":
            type = "IB";
            data = val[0].Ib.timestamp || [];
            break;
          case "IC":
            type = "IC";
            data = val[0].Ic.timestamp || [];
            break;
        }

        return data;
      };
      let labels = checkSensorType(datas);
      const formatDate = labels.map((label) => dayjs(label).format("DD-MM-YYYY HH:mm"));
      const count = formatDate.length;

      return {
        type: "category",
        labels: {
          align: "left",
          step: count <= 10 ? 0 : count <= 100 ? 1 : count <= 200 ? 2 : count <= 300 ? 3 : count <= 400 ? 4 : 5,
        },
        tickInterval: count <= 10 ? 0 : count <= 500 ? 15 : 100,
        categories: formatDate,
      };
    },
    checkUnit(currentSecondaryTabName: string) {
      let unit = "";
      if (currentSecondaryTabName === "Current") {
        unit = "(A)";
      } else if (currentSecondaryTabName === "Current Unbalance") {
        unit = "(%)";
      } else if (currentSecondaryTabName === "I0 (Ground Fault)") {
        unit = "Current (A)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.SPEED) {
        unit = "(RPM)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.THDi) {
        unit = "(%)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.ROTOR_BAR_CRACK) {
        unit = "(dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.ECCENTRIC_AIR_GAP) {
        unit = "(dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.MECHANICAL_FAILURE) {
        unit = "(dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.STATOR_SHORT_TURN) {
        unit = "(dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.BEARING) {
        unit = "(dB)";
      } else {
        unit = "%";
      }
      return unit;
    },
    series(datas: any) {
      const customLegend = {
        name: "All",
      };
      const tagCodeName: McaTimeSeriesTagCodeEnum = datas && datas[0].tagCode;

      this.currentSecondaryTabName = this.secondaryTabName;
      const dataArray = [];

      if (
        tagCodeName === McaTimeSeriesTagCodeEnum.I0 ||
        tagCodeName === McaTimeSeriesTagCodeEnum.SPEED ||
        tagCodeName === McaTimeSeriesTagCodeEnum.THDi ||
        this.primaryTabName === "Frequency Detail"
      ) {
        this.isShowDropdownSensorType = true;

        const checkSensorType = (val) => {
          val.forEach((res) => {
            let data = [];
            let tagCode = res.tagCode;
            let warning = res.warning;
            let alarm = res.alarm;
            let isAlert = res.isAlert;
            let unitName = this.secondaryTabName;

            switch (this.isSensorType.name) {
              case "IA":
                data = res.Ia.values || [];
                dataArray.push({
                  name: tagCode,
                  data,
                  tooltip: { valueSuffix: this.checkUnit(unitName) },
                  lineWidth: 2,
                  warning,
                  alarm,
                  isAlert,
                });
                break;
              case "IB":
                data = res.Ib.values || [];
                dataArray.push({
                  name: tagCode,
                  data,
                  tooltip: { valueSuffix: this.checkUnit(unitName) },
                  lineWidth: 2,
                  warning,
                  alarm,
                  isAlert,
                });
                break;
              case "IC":
                data = res.Ic.values || [];
                dataArray.push({
                  name: tagCode,
                  data,
                  tooltip: { valueSuffix: this.checkUnit(unitName) },
                  lineWidth: 2,
                  warning,
                  alarm,
                  isAlert,
                });
                break;
            }
          });
        };
        checkSensorType(datas);
      } else if (
        tagCodeName === McaTimeSeriesTagCodeEnum.CURRENT ||
        tagCodeName === McaTimeSeriesTagCodeEnum.PERCENT_UNBALANCE
      ) {
        this.isShowDropdownSensorType = false;

        datas.forEach((res) => {
          let tagCode = res.tagCode;
          let warning = res.warning;
          let alarm = res.alarm;
          let isAlert = res.isAlert;
          let unitName = this.secondaryTabName;

          // IA Series
          if (res.Ia && res.Ia.values) {
            dataArray.push({
              name: `${tagCode} - IA`,
              data: res.Ia.values,
              tooltip: { valueSuffix: this.checkUnit(unitName) },
              lineWidth: 2,
              color: "#FF0000",
              warning,
              alarm,
              isAlert,
            });
          }

          // IB Series
          if (res.Ib && res.Ib.values) {
            dataArray.push({
              name: `${tagCode} - IB`,
              data: res.Ib.values,
              tooltip: { valueSuffix: this.checkUnit(unitName) },
              lineWidth: 2,
              color: "#FFC41C",
              warning,
              alarm,
              isAlert,
            });
          }

          // IC Series
          if (res.Ic && res.Ic.values) {
            dataArray.push({
              name: `${tagCode} - IC`,
              data: res.Ic.values,
              tooltip: { valueSuffix: this.checkUnit(unitName) },
              lineWidth: 2,
              color: "#0070C0",
              warning,
              alarm,
              isAlert,
            });
          }
        });
      }

      const sers = [customLegend, ...dataArray];
      return sers;
    },
    async onChangeDate(ev: any) {
      this.startDate = dayjs(ev.startDate).subtract(7, "hour").format("YYYY-MM-DDTHH:mm:ss");
      this.endDate = dayjs(ev.endDate).subtract(7, "hour").format("YYYY-MM-DDTHH:mm:ss");
      await this.fetchResultData();
    },
    async reloadData() {
      await this.fetchResultData();
    },
    exportExcel() {
      // Export sensorType ex. IA, IB, IC
      const isExportAllType =
        this.currentSecondaryTabName === McaTimeSeriesTagCodeEnum.CURRENT ||
        this.currentSecondaryTabName === McaTimeSeriesTagCodeEnum.PERCENT_UNBALANCE ||
        this.currentSecondaryTabName === "Current" ||
        this.currentSecondaryTabName === "Current Unbalance";

      if (isExportAllType) {
        this.exportExcelAllType();
      } else {
        this.exportExcelByType();
      }
    },
    exportExcelByType() {
      if (this.chartResultData.length === 0) {
        console.error("No data to export.");
        return;
      }

      const name =
        `mca_${this.model.modelCode}_${this.$store.state.graphInformationTab.is_secondary_tab.name}_${this.isSensorType.name}`
          .toUpperCase()
          .replace(/\s+/g, "_");
      this.excelLoading = true;
      let data = [];

      switch (this.isSensorType.name) {
        case "IA":
          data = this.chartResultData.map((val) => val.Ia);
          break;
        case "IB":
          data = this.chartResultData.map((val) => val.Ib);
          break;
        case "IC":
          data = this.chartResultData.map((val) => val.Ic);
          break;
        default:
          console.error("Invalid sensor type.");
          return;
      }
      const result = {
        sname: `mca_${this.isSensorType.name}`.toUpperCase().replace(/\s+/g, "_"),
        data: data.map((val, key) => {
          const temp = val.values.map((valdata, idx) => {
            return {
              tagCode: this.chartResultData[key].tagCode,
              values: valdata,
              model_code: this.model.modelCode,
              status: valdata >= this.chartResultData[key].alarm ? 2 : valdata >= this.chartResultData[key].warning ? 1 : 0,
              timestamp: val.timestamp[idx],
            };
          });
          return temp;
        }),
      };
      this.exportToExcel(result, name);

      setTimeout(() => {
        this.excelLoading = false;
      }, 3000);
    },
    exportExcelAllType() {
      if (this.chartResultData.length === 0) {
        console.error("No data to export.");
        return;
      }

      const sensorTypes = ["IA", "IB", "IC"];
      const wb = XLSX.utils.book_new(); // Create a single workbook for all sensor types
      this.excelLoading = true;

      sensorTypes.forEach((sensorType) => {
        const sheetName = `MCA_${sensorType}`.toUpperCase().replace(/\s+/g, "_");
        let data = [];

        // Select data based on sensor type
        switch (sensorType) {
          case "IA":
            data = this.chartResultData.map((val) => val.Ia);
            break;
          case "IB":
            data = this.chartResultData.map((val) => val.Ib);
            break;
          case "IC":
            data = this.chartResultData.map((val) => val.Ic);
            break;
          default:
            console.error("Invalid sensor type.");
            return;
        }

        // Prepare the data for the Excel sheet
        const result = {
          sname: `mca_${this.isSensorType.name}`.toUpperCase().replace(/\s+/g, "_"),
          data: data.map((val, key) => {
            return val.values.map((valdata, idx) => ({
              tagCode: this.chartResultData[key].tagCode,
              values: valdata,
              model_code: this.model.modelCode,
              status: valdata >= this.chartResultData[key].alarm ? 2 : valdata >= this.chartResultData[key].warning ? 1 : 0,
              timestamp: val.timestamp[idx],
            }));
          }),
        };

        // Create a worksheet for the current sensor type and append it to the workbook
        const ws = XLSX.utils.json_to_sheet(result.data.flat());
        XLSX.utils.book_append_sheet(wb, ws, sheetName); // Add sheet to the workbook
      });

      // Export the workbook as a single file with multiple tabs
      try {
        const fileName =
          `MCA_${this.model.modelCode}_${this.$store.state.graphInformationTab.is_secondary_tab.name}_SENSORS.xlsx`
            .toUpperCase()
            .replace(/\s+/g, "_");
        XLSX.writeFile(wb, fileName);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
      }

      setTimeout(() => {
        this.excelLoading = false;
      }, 3000);
    },
    exportToExcel(result: any, fileName: string) {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(result.data.flat());
      XLSX.utils.book_append_sheet(wb, ws, result.sname); // => sheet name

      try {
        XLSX.writeFile(wb, `${fileName}.xlsx`);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
      }
    },
    updateAxis(axis: string) {
      const chart = this.$refs.chart.chart;
      const xAxis = chart.xAxis[0];
      const yAxis = chart.yAxis[0];

      this.xAxisMin =
        parseFloat(this.xAxisMin) > this.xAxisMaxRange
          ? this.xAxisMaxRange
          : parseFloat(this.xAxisMin) < 1
          ? 1
          : this.xAxisMin;
      this.xAxisMax =
        parseFloat(this.xAxisMax) > this.xAxisMaxRange
          ? this.xAxisMaxRange
          : parseFloat(this.xAxisMin) < 1
          ? 1
          : this.xAxisMax;

      const xAxisMin = this.xAxisMin ? parseFloat(this.xAxisMin) - 1 : undefined;
      const xAxisMax = this.xAxisMax ? parseFloat(this.xAxisMax) - 1 : undefined;
      const yAxisMin = this.yAxisMin ? parseFloat(this.yAxisMin) : undefined;
      const yAxisMax = this.yAxisMax ? parseFloat(this.yAxisMax) : undefined;

      if (axis === "x") {
        if (!isNaN(xAxisMin) && !isNaN(xAxisMax)) {
          xAxis.setExtremes(xAxisMin, xAxisMax);
        } else {
          xAxis.setExtremes(undefined, undefined);
        }
      }

      if (axis === "y") {
        if (!isNaN(yAxisMin) && !isNaN(yAxisMax)) {
          yAxis.setExtremes(yAxisMin, yAxisMax);
        } else {
          yAxis.setExtremes(undefined, undefined);
        }
      }
    },
    clearFilter() {
      const chart = this.$refs.chart.chart;
      const xAxis = chart.xAxis[0];
      const yAxis = chart.yAxis[0];

      this.xAxisMin = undefined;
      this.xAxisMax = undefined;
      this.yAxisMin = undefined;
      this.yAxisMax = undefined;

      xAxis.setExtremes(undefined, undefined);
      yAxis.setExtremes(undefined, undefined);

      if (chart.resetZoomButton) {
        chart.resetZoomButton.destroy();
        delete chart.resetZoomButton;
      }
    },
    showYAxisLabel(currentSecondaryTabName: string) {
      if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.CURRENT || currentSecondaryTabName === "Current") {
        return "Current (A)";
      } else if (
        currentSecondaryTabName === McaTimeSeriesTagCodeEnum.PERCENT_UNBALANCE ||
        currentSecondaryTabName === "Current Unbalance"
      ) {
        return "Current unbalance (%)";
      } else if (
        currentSecondaryTabName === McaTimeSeriesTagCodeEnum.I0 ||
        currentSecondaryTabName === "I0 (Ground Fault)"
      ) {
        return "Current (A)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.SPEED) {
        return "Speed (RPM)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.THDi) {
        return "THDi (%)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.ROTOR_BAR_CRACK) {
        return "Amplitude (dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.ECCENTRIC_AIR_GAP) {
        return "Amplitude (dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.MECHANICAL_FAILURE) {
        return "Amplitude (dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.STATOR_SHORT_TURN) {
        return "Amplitude (dB)";
      } else if (currentSecondaryTabName === McaTimeSeriesTagCodeEnum.BEARING) {
        return "values";
      } else {
        return "Values";
      }
    },
  },
  watch: {
    async isSecondaryTab() {
      await this.fetchResultData();
    },
    async isSensorType() {
      await this.fetchResultData();
    },
    async primaryTabName() {
      await this.fetchResultData();
    },
    async selectDateType() {
      if (this.selectDateType === "yearly") {
        this.isNormal = false;
      } else {
        this.isNormal = true;
      }
    },
  },
});
